import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tag,
} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';

const formatMoney = (symbol, amount) =>
  `${symbol || ''}${Number(amount).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

// Catalog prices persist their discount as PERCENTAGE/FIXED/NONE; the reminder
// rows speak VARIABLE/FIXED. Translate a catalog price's saved discount into a
// row, or null when there's nothing meaningful to carry over.
const CATALOG_DISCOUNT_TO_ROW_TYPE = {PERCENTAGE: 'VARIABLE', FIXED: 'FIXED'};

const existingDiscountFromPrice = price => {
  const discount = price?.discount;
  if (!discount || discount.type === 'NONE') return null;
  if (discount.value === null || discount.value === undefined) return null;
  const type = CATALOG_DISCOUNT_TO_ROW_TYPE[discount.type];
  if (!type) return null;
  return {type, value: Number(discount.value)};
};

/**
 * Per-reminder catalog tabs picker. Mirrors the announcement's
 * SubscriptionRenewalSection, but scoped to a single reminder
 * (`reminders[reminderName].catalogTabs`) and to the catalog items of the plan
 * being edited (no plan picker, no purpose gating). Each tab can carry one
 * discount per currency. Submits as catalogTabs[].appliedDiscount (an array of
 * {currency, type, value}); the form keeps an `applyDiscount` flag + `discounts`
 * rows, the same split the announcement uses.
 */
const ReminderCatalogTabs = ({
  reminderName,
  catalogItems,
  currencyOptions,
  discountTypes,
  form,
}) => {
  const basePath = ['reminders', reminderName, 'catalogTabs'];
  const catalogTabs = Form.useWatch(basePath, form) || [];

  const catalogOptions = catalogItems.map(item => ({
    value: item.key,
    label: (item.label && (item.label.EN || item.label.FR)) || item.key,
  }));

  const nextCurrency = used =>
    currencyOptions.find(opt => !used.includes(opt.value))?.value ||
    currencyOptions[0]?.value;

  const lookupExistingDiscount = (catalogKey, currency) => {
    if (!catalogKey || !currency) return null;
    const item = catalogItems.find(catalog => catalog.key === catalogKey);
    const price = item?.prices?.find(p => p.currency === currency);
    return existingDiscountFromPrice(price);
  };

  // Only one tab can be the default. Mutate the array in place so antd's
  // Form.List propagates the change to every controlled switch.
  const setDefaultTab = (rowIndex, checked) => {
    const tabs = form.getFieldValue(basePath) || [];
    for (let index = 0; index < tabs.length; index += 1) {
      if (checked) {
        tabs[index] = {...tabs[index], isDefault: index === rowIndex};
      } else if (index === rowIndex) {
        tabs[index] = {...tabs[index], isDefault: false};
      }
    }
    form.setFieldValue(basePath, tabs);
  };

  // Turning "Apply discount" on seeds a first currency row, pre-filled from the
  // catalog's existing discount when there is one.
  const setApplyDiscount = (rowIndex, checked) => {
    const tabs = form.getFieldValue(basePath) || [];
    const tab = tabs[rowIndex] || {};
    let {discounts} = tab;
    if (checked && !(tab.discounts && tab.discounts.length)) {
      const currency = nextCurrency([]);
      const existing = lookupExistingDiscount(tab.catalogKey, currency);
      discounts = [
        {
          type: existing?.type || 'FIXED',
          value: existing ? existing.value : null,
          currency,
        },
      ];
    }
    tabs[rowIndex] = {...tab, applyDiscount: checked, discounts};
    form.setFieldValue(basePath, tabs);
  };

  // Changing the catalog re-pulls the saved discount into each currency row.
  const handleCatalogChange = (rowIndex, catalogKey) => {
    const tabs = form.getFieldValue(basePath) || [];
    const tab = tabs[rowIndex];
    if (!tab) return;
    const discounts = (tab.discounts || []).map(disc => {
      const existing = lookupExistingDiscount(catalogKey, disc?.currency);
      return existing
        ? {...disc, type: existing.type, value: existing.value}
        : disc;
    });
    tabs[rowIndex] = {...tab, catalogKey, discounts};
    form.setFieldValue(basePath, tabs);
  };

  // Picking a currency pre-fills the row from that catalog price's discount.
  const handleCurrencyChange = (rowIndex, discIndex, currency) => {
    const tabs = form.getFieldValue(basePath) || [];
    const tab = tabs[rowIndex];
    if (!tab) return;
    const discounts = [...(tab.discounts || [])];
    const existing = lookupExistingDiscount(tab.catalogKey, currency);
    discounts[discIndex] = existing
      ? {
          ...discounts[discIndex],
          currency,
          type: existing.type,
          value: existing.value,
        }
      : {...discounts[discIndex], currency};
    tabs[rowIndex] = {...tab, discounts};
    form.setFieldValue(basePath, tabs);
  };

  const renderPreview = (tabIndex, discIndex) => {
    const tab = catalogTabs[tabIndex] || {};
    const disc = (tab.discounts || [])[discIndex] || {};
    const item = catalogItems.find(catalog => catalog.key === tab.catalogKey);
    const price = item?.prices?.find(p => p.currency === disc.currency);
    const symbol =
      currencyOptions.find(opt => opt.value === disc.currency)?.symbol || '';

    if (!tab.catalogKey || !disc.currency) {
      return <span style={{color: '#8b82a7'}}>Pick a currency</span>;
    }
    if (price?.amount === undefined || price?.amount === null) {
      return (
        <span style={{color: '#cf1322'}}>
          No catalog price for {disc.currency}
        </span>
      );
    }
    if (disc.value === undefined || disc.value === null || disc.value === '') {
      return (
        <span style={{color: '#8b82a7'}}>
          {formatMoney(symbol, price.amount)} → enter a value
        </span>
      );
    }

    const base = Number(price.amount);
    const newPrice =
      disc.type === 'VARIABLE'
        ? base - (base * Number(disc.value)) / 100
        : base - Number(disc.value);

    return (
      <span>
        <span style={{textDecoration: 'line-through', color: '#8b82a7'}}>
          {formatMoney(symbol, base)}
        </span>{' '}
        <Tag color="green">{formatMoney(symbol, Math.max(0, newPrice))}</Tag>
      </span>
    );
  };

  return (
    <Form.List name={[reminderName, 'catalogTabs']}>
      {(fields, {add, remove}) => (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}>
            <strong>Catalog tabs</strong>
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                add({
                  catalogKey: undefined,
                  isDefault: fields.length === 0,
                  applyDiscount: false,
                  discounts: [],
                })
              }>
              Add catalog tab
            </Button>
          </div>
          {!fields.length ? (
            <p style={{color: '#8b82a7'}}>
              No catalog tabs yet. Add at least one to upsell.
            </p>
          ) : null}
          {fields.map(field => {
            const row = catalogTabs[field.name] || {};
            return (
              <div
                key={field.key}
                style={{
                  background: '#fafafa',
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                }}>
                <Form.Item name={[field.name, 'id']} hidden>
                  <Input />
                </Form.Item>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}>
                  <Space>
                    <Switch
                      size="small"
                      checked={!!row.isDefault}
                      onChange={checked => setDefaultTab(field.name, checked)}
                    />
                    <span>Default tab</span>
                  </Space>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                </div>
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Catalog item"
                      name={[field.name, 'catalogKey']}
                      rules={[
                        {required: true, message: 'Required'},
                        {
                          validator: async (_, value) => {
                            if (
                              value &&
                              !catalogItems.some(item => item.key === value)
                            ) {
                              throw new Error('Item must belong to this plan');
                            }
                          },
                        },
                      ]}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Catalog item"
                        options={catalogOptions}
                        onChange={value =>
                          handleCatalogChange(field.name, value)
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Apply discount"
                      tooltip="Offer this catalog tab at a discounted renewal price (per currency).">
                      <Switch
                        checked={!!row.applyDiscount}
                        onChange={checked =>
                          setApplyDiscount(field.name, checked)
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {row.applyDiscount ? (
                  <Form.List name={[field.name, 'discounts']}>
                    {(
                      discFields,
                      {add: addDiscount, remove: removeDiscount},
                    ) => (
                      <div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                          }}>
                          <span>Discounts by currency</span>
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              const tab =
                                (form.getFieldValue(basePath) || [])[
                                  field.name
                                ] || {};
                              const used = (tab.discounts || [])
                                .map(d => d?.currency)
                                .filter(Boolean);
                              const currency = nextCurrency(used);
                              const existing = lookupExistingDiscount(
                                tab.catalogKey,
                                currency,
                              );
                              addDiscount({
                                type: existing?.type || 'FIXED',
                                value: existing ? existing.value : null,
                                currency,
                              });
                            }}>
                            Add currency
                          </Button>
                        </div>
                        {!discFields.length ? (
                          <p style={{color: '#8b82a7'}}>
                            No discounts yet. Add one per currency.
                          </p>
                        ) : null}
                        {discFields.map(discField => {
                          const disc =
                            (row.discounts || [])[discField.name] || {};
                          const isVariable = disc.type === 'VARIABLE';
                          return (
                            <Row key={discField.key} gutter={12}>
                              <Col xs={12} md={5}>
                                <Form.Item
                                  label="Currency"
                                  name={[discField.name, 'currency']}
                                  rules={[
                                    {required: true, message: 'Required'},
                                  ]}>
                                  <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={currencyOptions}
                                    onChange={value =>
                                      handleCurrencyChange(
                                        field.name,
                                        discField.name,
                                        value,
                                      )
                                    }
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={5}>
                                <Form.Item
                                  label="Type"
                                  name={[discField.name, 'type']}
                                  rules={[
                                    {required: true, message: 'Required'},
                                  ]}>
                                  <Select options={discountTypes} />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={6}>
                                <Form.Item
                                  label={
                                    isVariable ? 'Value (%)' : 'Value (amount)'
                                  }
                                  name={[discField.name, 'value']}
                                  rules={[
                                    {required: true, message: 'Required'},
                                    {
                                      type: 'number',
                                      min: isVariable ? 0 : 0.01,
                                      max: isVariable ? 100 : undefined,
                                      message: isVariable
                                        ? 'Use 0-100'
                                        : 'Must be > 0',
                                    },
                                  ]}>
                                  <InputNumber
                                    min={0}
                                    style={{width: '100%'}}
                                    placeholder={
                                      isVariable ? 'e.g. 20' : 'e.g. 5.00'
                                    }
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={20} md={6}>
                                <Form.Item label="New price (preview)">
                                  {renderPreview(field.name, discField.name)}
                                </Form.Item>
                              </Col>
                              <Col xs={4} md={2}>
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeDiscount(discField.name)}
                                  style={{marginTop: 30}}
                                />
                              </Col>
                            </Row>
                          );
                        })}
                      </div>
                    )}
                  </Form.List>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Form.List>
  );
};

export default ReminderCatalogTabs;
